import { useEffect, useRef } from 'react';
import { create } from 'zustand';

import { useAdminOverviewApi } from '../../../common/hooks/use-admin-overview-api.hook';
import { useAdminSignupsApi } from '../../../common/hooks/use-admin-signups-api.hook';
import { useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import type { OverviewResponse } from '../../../common/models/admin-overview.model';

/** How often the sidebar's counts are refreshed (the overview itself is cached a minute server-side). */
const REFRESH_MS = 5 * 60_000;

interface AdminNavState {
  quotesNew: number;
  invoicesOverdue: number;
  jobsStuck: number;
  /** Providers we pay whose balance is low (the costs page). */
  costsAttention: number;
  /** Self-serve accounts waiting for a person to approve them (the Signups page). */
  signupsPending: number;
  setSignupsPending: (n: number) => void;
  /** Whether this admin is on the CRM allowlist (the API 404s everyone else). Gates the CRM menu entry. */
  crmAllowed: boolean;
  setCrmAllowed: (allowed: boolean) => void;
  /** The overview page hands over what it just loaded, so the counts never lag the page. */
  setFromOverview: (o: OverviewResponse) => void;
}

export const useAdminNav = create<AdminNavState>((set) => ({
  quotesNew: 0,
  invoicesOverdue: 0,
  jobsStuck: 0,
  costsAttention: 0,
  signupsPending: 0,
  setSignupsPending: (n) => set({ signupsPending: Math.max(0, Number(n) || 0) }),
  crmAllowed: false,
  setCrmAllowed: (allowed) => set({ crmAllowed: allowed }),
  setFromOverview: (o) => {
    const jobs = o.health.jobs;
    set({
      quotesNew: o.sales.quotesNew,
      invoicesOverdue: o.sales.invoicesOverdue,
      // The queues the Job queues page shows, where they can be retried or cleaned up.
      jobsStuck: jobs.imports.stuck + jobs.seamless.stuck,
      costsAttention: o.health.costs?.attention || 0,
    });
  },
}));

/** Keeps the sidebar badges (new quotes, overdue invoices, stuck jobs) and the CRM entry current. Mounted once in the super-admin shell. */
export function AdminNavSync() {
  const api = useAdminOverviewApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const signupsApi = useAdminSignupsApi();
  const signupsApiRef = useRef(signupsApi);
  signupsApiRef.current = signupsApi;
  const crmApi = useCrmApi();
  const crmApiRef = useRef(crmApi);
  crmApiRef.current = crmApi;

  // CRM access is per admin and does not change during a session: ask once.
  useEffect(() => {
    Promise.resolve(crmApiRef.current.checkAccess())
      .then((r) => useAdminNav.getState().setCrmAllowed(!!r?.allowed))
      .catch(() => useAdminNav.getState().setCrmAllowed(false));
  }, []);

  useEffect(() => {
    let stopped = false;
    const load = () => {
      apiRef.current
        .getOverview(7)
        .then((o) => !stopped && o?.sales && useAdminNav.getState().setFromOverview(o))
        .catch(() => undefined);
      // An API without the signups module answers 404: the badge simply stays at 0.
      Promise.resolve(signupsApiRef.current.stats(30))
        .then((s) => !stopped && s && typeof s.pendingReview === 'number' && useAdminNav.getState().setSignupsPending(s.pendingReview))
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, []);

  return null;
}
